/**
 * @typedef {import('./BlogPost').default} BlogPost
 * @typedef {import('./BlogPostExcerpt').default} BlogPostExcerpt
 * @typedef {import('./GetsAllPostTags').HubspotPostsResponse} HubspotPostsResponse
 * @typedef {import('./GetsBlogPost').default} GetsBlogPost
 * @typedef {import('./GetsBlogPostExcerpts').default} GetsBlogPostExcerpts
 * @typedef {import('@mooveguru/js-http-client/GetsHttp').default} httpClient
 * @typedef {Readonly<{
 *   authorName?: string,
 *   authorFullName?: string,
 *   postSummary?: string,
 *   description?: string,
 *   publishDate?: string,
 *   publishedDate?: string,
 *   featuredImage?: string,
 *   featuredImageUrl?: string,
 *   slug?: string,
 *   url?: string,
 *   name?: string,
 *   title?: string,
 *   tagIds?: number[],
 *   tags?: number[]
 * }>} BlogPostExcerptSource
 */

import config from '../../config/local/blog';
import { hubspotPaginationPageToAfter } from '../../utils/hubspot';
import images from '../../config/local/images';
/**
 * @implements {GetsBlogPostExcerpts}
 * @implements {GetsBlogPost}
 */
export default class BlogService {
	/** @param  {httpClient} httpClient*/
	constructor(httpClient) {
		this.httpClient = httpClient;
	}

	/** @returns {Promise<?BlogPostExcerpt>} */
	async getLatestPostExcerpt() {
		const searchParamsBuilder = new URLSearchParams();
		searchParamsBuilder.append('_v', config.version);
		searchParamsBuilder.append('limit', '1');
		const searchParams = searchParamsBuilder.toString();
		const url =
			searchParams === ''
				? config.api.posts
				: `${config.api.posts}?${searchParams}`;
		const response = await this.httpClient.get(url);
		if (!response.isOk) {
			throw new Error(response.body?.message);
		}
		const data = response.body?.results?.[0];
		if (data === undefined) {
			return null;
		}
		return {
			author: {
				headshotUrl: config.defaultAuthorImage, // not provided by API,
				name: data.authorName,
			},
			content: data.postSummary,
			date: new Date(data.publishDate),
			imageUrl: data.featuredImage || images.blog.default.medium,
			slug: data.slug.split('/').pop(),
			tagIds: data.tagIds,
			title: data.name,
		};
	}

	/**
	 * @param {string} slug
	 * @returns {Promise<BlogPost?>}
	 */
	async getPost(slug) {
		const response = await this.httpClient.get(
			`${config.api.posts}/${slug}`
		);
		if (!response.isOk) {
			throw new Error(response.body?.message);
		}
		const data = response.body.results?.[0];
		if (data === undefined) {
			return null;
		}
		return {
			author: {
				headshotUrl: config.defaultAuthorImage, // not provided by API,
				name: data.authorName,
			},
			content: data.postBody,
			date: new Date(data.publishDate),
			imageUrl: data.featuredImage || images.blog.default.medium,
			title: data.name,
		};
	}

	/**
	 * @returns {Promise<?import('./GetsAllPostTags').BlogPostTags>}
	 */
	async getAllPostTags() {
		const searchParamsBuilder = new URLSearchParams();
		searchParamsBuilder.append('limit', '100');
		const searchParams = searchParamsBuilder.toString();
		const response = await this.httpClient.get(
			`${config.api.tags}?${searchParams}`
		);
		if (!response.isOk) {
			throw new Error(response.body?.message);
		}
		const data = response.body.results;
		if (data === undefined) {
			return null;
		}
		return {
			tags: response.body.results?.map(
				(
					/** @type {{  name: string; id: string; slug: string }} */ tag
				) => ({
					slug: tag.slug,
					tagId: tag.id,
					tagName: tag.name,
				})
			),
		};
	}

	/**
	 * @param {{
	 *   limit: number,
	 *   after?: number,
	 *   state: string,
	 *   sort: string,
	 *   tagId__eq?: string
	 * }} params
	 * @returns {Promise<HubspotPostsResponse>}
	 */
	async getFilterdPostTags(params) {
		const searchParamsBuilder = new URLSearchParams();

		for (const [key, value] of Object.entries(params)) {
			if (value === null || value === undefined) {
				continue;
			}
			searchParamsBuilder.set(key, String(value));
		}

		const response = await this.httpClient.get(
			`${config.api.posts}?${searchParamsBuilder.toString()}`
		);

		if (!response.isOk) {
			throw new Error(response.body?.message);
		}

		return response.body;
	}

	/**
	 * @param {string | undefined} topLevelTagId
	 * @returns {Promise<{ results: BlogPostExcerpt[] }>}
	 */
	async getAllTagIds(topLevelTagId) {
		let after; // number | undefined
		const categorizedPostResults = [];

		do {
			/** @type {HubspotPostsResponse} */
			const data = await this.getFilterdPostTags({
				after,
				limit: 100,
				sort: '-publishDate',
				state: 'PUBLISHED',
				// eslint-disable-next-line camelcase -- required for API parameter
				tagId__eq: topLevelTagId,
			});

			categorizedPostResults.push(
				...(data?.results ?? []).map(BlogService.#mapPostExcerpt)
			);
			after = data?.paging?.next?.after;
		} while (after !== undefined);

		return {
			results: categorizedPostResults,
		};
	}

	/**
	 * @param {string} searchQuery
	 * @param {number} page
	 * @param {number} [perPage]
	 * @param {string} [categoryId]
	 * @returns {Promise<{posts: BlogPostExcerpt[], totalPages: number}>}
	 */

	async getPostExcerpts(
		searchQuery,
		page,
		// eslint-disable-next-line default-param-last
		perPage = config.perPage.listing,
		categoryId
	) {
		const searchParams = BlogService.#buildExcerptSearchParams(
			searchQuery,
			page,
			perPage,
			categoryId
		);
		const url = `${
			searchQuery === '' ? config.api.posts : config.api.search
		}?${searchParams}`;
		const response = await this.httpClient.get(url);
		if (!response.isOk) {
			throw new Error(response.body?.message);
		}
		return {
			posts: response.body.results?.map(BlogService.#mapPostExcerpt),
			totalPages: BlogService.#calculateTotalPages(
				response.body.total,
				perPage
			),
		};
	}

	/**
	 * @param {string | undefined} searchQuery
	 * @param {number} page
	 * @param {number} perPage
	 * @param {string | undefined} categoryId
	 * @returns {string}
	 */
	static #buildExcerptSearchParams(searchQuery, page, perPage, categoryId) {
		const searchParamsBuilder = new URLSearchParams();
		searchParamsBuilder.append('limit', perPage.toFixed(0));
		if (searchQuery) {
			searchParamsBuilder.append('q', searchQuery);
		}

		if (categoryId) {
			searchParamsBuilder.append('tagId__eq', categoryId);
		}

		if (page && page > 1) {
			searchParamsBuilder.append(
				'after',
				hubspotPaginationPageToAfter(page - 1, perPage)
			);
		}
		return searchParamsBuilder.toString();
	}

	/**
	 * @param {number} total
	 * @param {number} perPage
	 * @returns {number}
	 */
	static #calculateTotalPages(total, perPage) {
		return total > 0 ? Math.ceil(total / perPage) : 0;
	}

	/**
	 * @param {BlogPostExcerptSource} data
	 * @returns {BlogPostExcerpt}
	 */
	static #mapPostExcerpt(data) {
		return {
			author: {
				headshotUrl: config.defaultAuthorImage, // not provided by API
				name: BlogService.#getValue(
					[data.authorName, data.authorFullName],
					''
				),
			},
			content: BlogService.#getValue(
				[data.postSummary, data.description],
				''
			),
			date: new Date(
				BlogService.#getValue(
					[data.publishDate, data.publishedDate],
					''
				)
			),
			imageUrl: BlogService.#getValue(
				[data.featuredImage, data.featuredImageUrl],
				images.blog.default.medium
			),
			slug: BlogService.#toSlug(
				BlogService.#getValue([data.slug, data.url], '')
			),
			tagIds: BlogService.#getValue(
				[data.tagIds, data.tags],
				/** @type {number[]} */ ([])
			),
			title: BlogService.#getValue([data.name, data.title], ''),
		};
	}

	/**
	 * @template T
	 * @param {(T | undefined | null)[]} values
	 * @param {T} fallbackValue
	 * @returns {T}
	 */
	static #getValue(values, fallbackValue) {
		const value = values.find(Boolean);

		return value === undefined || value === null ? fallbackValue : value;
	}

	/**
	 * @param {string} value
	 * @returns {string}
	 */
	static #toSlug(value) {
		return value.split('/').pop() || '';
	}
}
